import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewEncapsulation, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { RouterModule } from "@angular/router";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { NgxMatTimepickerModule } from "ngx-mat-timepicker";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { VFIconComponent } from "../../../vficon/vficon.component";
import { CustomDropdownComponent } from "../../../custom-dropdown/custom-dropdown.component";
import { ExclusionTypeConfig, TableCell, TableColumn, TableRow } from "../../../../models/common-table";
import { CellTypeRendererComponent } from "./cell-type-renderer/cell-type-renderer.component";
import { CellSubtypeRendererComponent } from "./cell-subtype-renderer/cell-subtype-renderer.component";

export interface CommonTableGridHandlers {
  buildElementId: (section: string, rowIndex?: number, columnIndex?: number, itemIndex?: number) => string;
  trackByColumn: (index: number, column: TableColumn) => string;
  handleColumnTooltipClick: (tooltip?: string) => void;
  extractUrl: (tooltip?: string) => string | undefined;
  sortDataByColumn: (column: TableColumn, columnIndex: number) => void;
  selectAllRows: (event: Event, rows: TableRow[]) => void;
  isRowInEditMode: (rowIndex: number) => boolean;
  notifyCellChange: (
    event: unknown,
    data: unknown,
    column: string,
    row: TableRow,
    options?: Record<string, unknown> | null,
    action?: string,
  ) => void;
  copyValue: (value: string) => void;
  getStateClass: (state: string) => string;
  getButtonConfig: (state: string) => { text: string; cssClass: string; disabled: boolean };
  toggleEditMode: (rowIndex: number) => void;
  deleteRow: (row: TableRow) => void;
  saveRowChanges: (row: TableRow, rowIndex: number) => void;
  cancelRowEdit: (rowIndex: number) => void;
  handleExclusionAdd: (
    resources: Array<{ exclude?: Array<{ key: string; value: string }> }>,
    type: "start" | "stop",
  ) => void;
  columnNotExcluded: (column: TableColumn | TableCell<unknown>, index: number) => boolean;
  columnTotal: (index: number) => string;
  columnMomTrend: (index: number) => string;
  isGroupSchedulerTable: () => boolean;
}

@Component({
  selector: "app-common-table-grid",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TooltipModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    NgxMatTimepickerModule,
    FontAwesomeModule,
    VFIconComponent,
    CustomDropdownComponent,
    CellTypeRendererComponent,
    CellSubtypeRendererComponent,
  ],
  templateUrl: "./common-table-grid.component.html",
  styleUrls: ["../../common-table.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class CommonTableGridComponent implements AfterViewInit, OnDestroy {
  private el = inject(ElementRef);
  private observer!: MutationObserver;

  @Input() tableName = "";
  @Input() tableDomId = "common-table";
  @Input() columns: TableColumn[] = [];
  @Input() currentPage: TableRow[] = [];
  @Input() tableRows: TableRow[] = [];
  @Input() customGrandTotal: TableRow = [];
  @Input() showSelectAllCheckbox = false;
  @Input() areAllRowsSelected = false;
  @Input() sortOrders: Record<string, "ASC" | "DESC"> = {};
  @Input() backgroundColors: { warning: string; success: string; danger: string; white: string; default: string } = {
    warning: "rgb(255, 255, 0, 0.2)",
    success: "rgb(144, 238, 144, 0.2)",
    danger: "rgb(250, 0, 0, 0.2)",
    white: "rgb(255, 255, 255, 0.2)",
    default: "auto",
  };
  @Input() emptyValues: string[] = [];
  @Input() weekDays: unknown[] = [];
  @Input() daysOfWeek: string[] = [];
  @Input() findColumnValueTotal = false;
  @Input() computeMomTrend = false;
  @Input() exclusionTypes: ExclusionTypeConfig[] = [];
  @Input() handlers!: CommonTableGridHandlers;

  public faArrowUp = faArrowUp;
  public faArrowDown = faArrowDown;
  public isArray = Array.isArray;

  ngAfterViewInit(): void {
    this.observer = new MutationObserver(() => {
      const headers: NodeListOf<HTMLElement> = this.el.nativeElement.querySelectorAll("th.resizable");
      if (headers.length === 0) {
        return;
      }

      for (let index: number = 0; index < headers.length; index++) {
        this.makeResizable(headers[index]);
      }
      this.observer.disconnect();
    });

    this.observer.observe(this.el.nativeElement, { childList: true, subtree: true });
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private makeResizable(tableHeader: HTMLElement): void {
    Object.assign(tableHeader.style, {
      position: "relative",
      overflow: "hidden",
    });

    const existingResizer: HTMLSpanElement | null = tableHeader.querySelector(".col-resizer");
    if (existingResizer) {
      return;
    }

    const resizer: HTMLSpanElement = document.createElement("span");
    resizer.classList.add("col-resizer");

    Object.assign(resizer.style, {
      width: "5px",
      height: "100%",
      position: "absolute",
      top: "0",
      right: "0",
      cursor: "ew-resize",
      userSelect: "none",
      zIndex: "10",
    });
    tableHeader.appendChild(resizer);

    resizer.addEventListener("mousedown", (event: MouseEvent): void => {
      event.preventDefault();
      const startX: number = event.pageX;
      const startWidth: number = tableHeader.offsetWidth;

      const onMouseMove: (mouseEvent: MouseEvent) => void = (mouseEvent: MouseEvent): void => {
        const newWidth: number = this.calculateNewWidth(tableHeader, startWidth, startX, mouseEvent.pageX);
        this.applyNewWidth(tableHeader, newWidth, startWidth);
      };

      const onMouseUp: () => void = (): void => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    });
  }

  private calculateNewWidth(tableHeader: HTMLElement, startWidth: number, startX: number, currentX: number): number {
    const headerContent: HTMLElement | null = tableHeader.querySelector(".header-content");
    const minimumWidth: number = headerContent?.offsetWidth ?? 100;
    return Math.max(minimumWidth, startWidth + (currentX - startX));
  }

  private applyNewWidth(tableHeader: HTMLElement, newWidth: number, startWidth: number): void {
    tableHeader.style.width = `${newWidth}px`;

    const headerContent: HTMLElement | null = tableHeader.querySelector(".header-content");
    if (headerContent) {
      headerContent.style.width = `${newWidth}px`;
    }

    const rowHeader: HTMLElement | null = tableHeader.parentElement;
    if (!rowHeader) {
      return;
    }

    const columnIndex: number = Array.from(rowHeader.children).indexOf(tableHeader);
    const tableElement: HTMLElement | null = tableHeader.closest("table");
    if (!tableElement) {
      return;
    }

    const rows: NodeListOf<HTMLElement> = tableElement.querySelectorAll("tbody tr");

    for (let index: number = 0; index < rows.length; index++) {
      const row: HTMLElement = rows[index];
      const cell: HTMLElement | undefined = row.children[columnIndex] as HTMLElement;
      if (!cell) {
        continue;
      }

      cell.style.width = `${newWidth}px`;

      const cellContent: HTMLElement | null = cell.querySelector("span.d-inline-block");
      if (cellContent) {
        cellContent.style.width = `${newWidth}px`;
        const delta: number = newWidth - startWidth;
        cellContent.classList.toggle("truncate", delta < -5);
      }
    }
  }

  public getTooltip(tooltip: string | string[] | undefined): string {
    if (Array.isArray(tooltip)) {
      return tooltip[0] || "";
    }
    return tooltip || "";
  }

  public getPlaceholder(placeholder: string | string[] | undefined): string {
    if (Array.isArray(placeholder)) {
      return placeholder[0] || "";
    }
    return placeholder || "";
  }

  public getIconName(icons: string | string[] | undefined): string {
    if (Array.isArray(icons)) {
      return icons[0] || "";
    }
    return icons || "";
  }
}
